import React from "react";
import cn from "classnames";
import { useQuery } from "@apollo/client";
import Spinner from "components/Spinner";
import SortableTable from "components/SortableTable";
import { Price, priceSort } from "components/Domain/Pricing";
import Link from "next/link";
import ListingBadge from "components/ListingBadge";
import { constantCase, formatNumber } from "util/strings";
import Save from "components/Save";
import { analyticsEvents, track } from "util/analytics";
import { trackInquireClick } from "util/ga4";
import { BUILDING_AVAILABILITY_QUERY_buildingOrRedirect_building_listings as Listing } from "./types/BUILDING_AVAILABILITY_QUERY";
import { BUILDING_AVAILABILITY_QUERY } from "./MobileView";
import styles from "./styles.module.scss";

interface IProps {
  buildingId: string;
  selectedListingId?: string;
}

const Loading = () => (
  <>
    <h1>Availability</h1>
    <Spinner />
  </>
);

export const BuildingAvailability = ({
  buildingId,
  selectedListingId,
}: IProps) => {
  const { data, loading } = useQuery(BUILDING_AVAILABILITY_QUERY, {
    variables: { buildingId },
  });

  if (loading) return <Loading />;

  const {
    buildingOrRedirect: { building },
  } = data;

  const trackEvent = () =>
    track(analyticsEvents.clicked, {
      category: `${selectedListingId ? "Listing" : "Building"} Page CTAs`,
      label: "Availability Table Inquiry",
    });

  return (
    <div
      className={cn(styles.tableContainer, {
        [styles.truncate]: !!selectedListingId,
      })}
    >
      <SortableTable
        buildingAdmin={false}
        defaultDirection="ASC"
        className={styles.table}
        styles={{}}
        headings={["Unit", "Size", "Seats", "Price", ""]}
        sorts={[
          (a: Listing, b: Listing) =>
            (parseInt(a.floorAndSuite.replace(/\D+/g, ""), 10) || 0) -
            (parseInt(b.floorAndSuite.replace(/\D+/g, ""), 10) || 0),
          (a: Listing, b: Listing) =>
            (a.squareFeet || a.estimatedSquareFeet) -
            (b.squareFeet || b.estimatedSquareFeet),
          (a: Listing, b: Listing) =>
            (a.maxOccupancy || a.estimatedMaxOccupancy) -
            (b.maxOccupancy || b.estimatedMaxOccupancy),
          (a: Listing, b: Listing) =>
            priceSort(a, b, building.neighborhood?.yearlyPricePerFoot || 0),
          null,
        ]}
        data={building.listings}
        defaultSort={1}
        renderRow={(listing: Listing) => (
          <tr
            className={
              listing.id === selectedListingId ? styles.selectedRow : ""
            }
            key={`listing-row-${listing.id}`}
          >
            <td>
              <Link
                href={`/building${building.slug}/${building.identity}?listingId=${listing.id}`}
                passHref
              >
                <a style={{ marginRight: "12px" }}>{listing.floorAndSuite}</a>
              </Link>
              {listing.brokerInsights && !!listing.brokerInsights.length && (
                <ListingBadge
                  className={styles.buildingBrokerInsight}
                  type="brokerInsight"
                />
              )}
            </td>
            <td>
              {listing.squareFeet ? (
                formatNumber(listing.squareFeet)
              ) : (
                <>
                  <span>~</span> {formatNumber(listing.estimatedSquareFeet)}
                </>
              )}
              <abbr>sqft</abbr>
            </td>
            <td>
              {listing.maxOccupancy ? (
                formatNumber(listing.maxOccupancy)
              ) : (
                <>
                  <span>~</span> {formatNumber(listing.estimatedMaxOccupancy)}
                </>
              )}{" "}
              seats
            </td>
            <td>
              <Price
                leaseType={listing.leaseType}
                status={listing.status}
                displayPSF={listing.displayPSF}
                nhoodPsf={building.neighborhood?.yearlyPricePerFoot}
                small
              />
            </td>
            <td>
              <Save
                inquiribleId={listing.id}
                signUpType={selectedListingId ? "Listing" : "Building"}
                type="Listing"
                source="Availability Table Inquiry"
                areaExternalIds={
                  building.neighborhood?.externalId
                    ? [building.neighborhood.externalId]
                    : []
                }
                marketOfInterest={building.market?.externalId}
                cityOfInterest={constantCase(building.city)}
              >
                {(saveListing, isSaved, saveLoading) => (
                  <button
                    type="button"
                    className={`btn-outline ${styles.btn}`}
                    disabled={isSaved || saveLoading}
                    onClick={(e) => {
                      trackEvent();
                      trackInquireClick({
                        listingId: listing.id,
                        buildingName: building.address || building.propertyName || '',
                        city: building.city || '',
                      });
                      saveListing(e);
                    }}
                  >
                    Inquire
                  </button>
                )}
              </Save>
            </td>
          </tr>
        )}
      >
        <tr>
          <td data-qa="availability-no-listings" colSpan={4}>
            There aren&rsquo;t any publicly available listings in this building
            right now. Inquire about this space and we&rsquo;ll help you find
            the right fit!
          </td>
        </tr>
      </SortableTable>
    </div>
  );
};

export default BuildingAvailability;
